import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Control,
  ControlCategories,
  ControlCategory,
  ControlCode,
  getControlCategory,
  getControlCategoryName,
  getControlName,
  InheritBehaviorFromCodeMappings,
  isDataControl,
  isEndpointManagementControl,
  isValidControl,
  isVulnerabilityManagementControl,
  queries,
  useAuthConfig,
  ValidControl,
} from "@theprelude/core";
import {
  Button,
  ChevronIcon,
  ControlCategoryIcon,
  ControlIcon,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
  DropdownSub,
  DropdownSubContent,
  DropdownSubTrigger,
  DropdownTrigger,
  ExceptionIcon,
  Loading,
  PlatformIcon,
  ResourceTypeIcon,
  ShieldFile,
  Truncate,
} from "@theprelude/ds";

export const AddExceptionDropdown = () => {
  const authConfig = useAuthConfig();
  const { data: account } = useSuspenseQuery(
    queries.iam.getAccount(authConfig),
  );
  const categories = getControlCategories(account.controls);

  return (
    <Dropdown>
      <DropdownTrigger asChild>
        <Button
          intent="pill"
          size="tiny"
          icon={<ExceptionIcon className="text-secondary-foreground" />}
        >
          Add exception
        </Button>
      </DropdownTrigger>
      <DropdownContent align="end" className="max-h-96 overflow-auto">
        {categories.map((category) => (
          <DropdownItem key={category}>
            <Link
              to={window.location.pathname}
              params={(prev) => prev}
              search={(prev) => ({
                ...prev,
                overlay: ["add-object-exception", { category }],
              })}
              className="flex items-center gap-2 pr-6"
            >
              <ControlCategoryIcon
                category={category}
                className="text-secondary-foreground"
              />
              {getControlCategoryName(category)}
            </Link>
          </DropdownItem>
        ))}
        <DropdownSeparator />
        <PoliciesDropdownItem controls={account.controls} />
      </DropdownContent>
    </Dropdown>
  );
};

const PoliciesDropdownItem = ({ controls }: { controls: Control[] }) => (
  <DropdownSub>
    <DropdownSubTrigger className="min-w-0 cursor-pointer bg-surface-background p-2 text-sm outline-none hover:bg-secondary-background">
      <div className="flex items-center gap-2">
        <ShieldFile className="text-secondary-foreground" />
        <div className="flex-1">
          <Truncate text="Policy">Policy</Truncate>
        </div>
        <ChevronIcon className="rotate-90 text-secondary-foreground" />
      </div>
    </DropdownSubTrigger>
    <DropdownSubContent className="z-30 max-h-96 overflow-auto">
      {controls
        .filter(
          (c): c is ValidControl =>
            !isEndpointManagementControl(c.id) &&
            !isDataControl(c.id) &&
            !isVulnerabilityManagementControl(c.id),
        )
        .sort((a, b) =>
          getControlName(a.id).localeCompare(getControlName(b.id)),
        )
        .map((control) => (
          <DropdownSub key={control.instance_id}>
            <DropdownSubTrigger className="min-w-0 cursor-pointer bg-surface-background p-2 text-sm outline-none hover:bg-secondary-background">
              <div className="flex items-center gap-2">
                <ControlIcon
                  control={control.id}
                  className="text-secondary-foreground"
                />
                <div className="flex flex-1 items-center gap-1">
                  {getControlName(control.id)}
                  {control.name && (
                    <span className="text-secondary-foreground text-xs">
                      ({control.name})
                    </span>
                  )}
                </div>
                <ChevronIcon className="rotate-90 text-secondary-foreground" />
              </div>
            </DropdownSubTrigger>
            <DropdownSubContent className="z-30 max-h-96 overflow-auto">
              <PolicyListDropdown control={control} />
            </DropdownSubContent>
          </DropdownSub>
        ))}
    </DropdownSubContent>
  </DropdownSub>
);

const PolicyListDropdown = ({ control }: { control: ValidControl }) => {
  const authConfig = useAuthConfig();
  const { data: response, isLoading } = useQuery(
    queries.scm.policies(authConfig, control.id, control.instance_id),
  );

  if (isLoading) {
    return (
      <div className="flex min-w-48 items-center justify-center p-4">
        <Loading />
      </div>
    );
  }

  const policies = [
    ...(response?.endpoint_evaluation?.policies ?? []),
    ...(response?.inbox_evaluation?.policies ?? []),
    ...(response?.user_evaluation?.policies ?? []),
  ].sort((a, b) => a.name.localeCompare(b.name));

  if (policies.length === 0) {
    return <div className="p-4 text-secondary-foreground">No policies.</div>;
  }

  return policies.map((policy) => (
    <DropdownItem key={`${policy.id}-${policy.name}`}>
      <Link
        to={window.location.pathname}
        params={(prev) => prev}
        search={(prev) => ({
          ...prev,
          overlay: [
            "add-policy-exception",
            {
              control: control.id,
              instanceId: control.instance_id,
              policyId: policy.id,
            },
          ],
        })}
        className="flex flex-1 items-center gap-2"
      >
        {"endpoint_count" in policy && (
          <PlatformIcon
            platform={policy.platform}
            className="text-secondary-foreground"
          />
        )}
        {"inbox_count" in policy && (
          <ResourceTypeIcon
            resourceType="inbox"
            className="text-secondary-foreground"
          />
        )}
        {"user_count" in policy && (
          <ResourceTypeIcon
            resourceType="user"
            className="text-secondary-foreground"
          />
        )}
        {policy.name}
      </Link>
    </DropdownItem>
  ));
};

const getControlCategories = (controls: Control[]): ControlCategory[] => {
  const categories = new Set(
    controls.map((control) => getControlCategory(control.id)),
  );

  controls.forEach((control) => {
    const behavesAsControl: ControlCode | undefined = isValidControl(control.id)
      ? InheritBehaviorFromCodeMappings[control.id]
      : control.id;

    if (behavesAsControl !== undefined) {
      categories.add(getControlCategory(behavesAsControl));
    }
  });

  return [...categories]
    .sort((a, b) =>
      getControlCategoryName(a).localeCompare(getControlCategoryName(b)),
    )
    .filter(
      (category) =>
        category !== ControlCategories.INVALID &&
        category !== ControlCategories.SIEM &&
        category !== ControlCategories.PRIVATE_REPO,
    );
};
